import React, { useState, useEffect, useCallback } from 'react';
import { FiPlus, FiMinus, FiRefreshCw } from 'react-icons/fi';
import useEditorStore from '../../../stores/useEditorStore';
import { ColorPicker } from '../../common';
import { panelStyle, titleStyle, sectionStyle, labelStyle, inputStyle, selectStyle } from '../../../styles/panelStyles';
import {
  createChartFabricImage,
  createDefaultChartData,
  CHART_TYPES,
  CHART_SUB_TYPES,
  LEGEND_POSITIONS,
  DEFAULT_CHART_COLORS,
} from '../../../utils/chartUtils';

// --- 차트 메타데이터 JSON 파싱 헬퍼 ---
const parseJSON = (value, fallback = null) => {
  if (value == null) return fallback;
  if (typeof value !== 'string') return value;
  try { return JSON.parse(value); } catch { return fallback; }
};

// --- 차트 추가 UI (차트 미선택 시) ---
const ChartAddSection = () => {
  const { canvas, canvasSize, pushHistory } = useEditorStore();
  const syncLayers = useEditorStore(s => s.syncLayersFromCanvas);

  const handleAddChart = async (chartType) => {
    if (!canvas) return;

    const defaultData = createDefaultChartData(chartType);
    const width = Math.round(canvasSize.width * 0.6);
    const height = Math.round(canvasSize.height * 0.45);

    const img = await createChartFabricImage(defaultData, width, height);
    img.set({
      left: canvasSize.width / 2,
      top: canvasSize.height / 2,
      originX: 'center',
      originY: 'center',
      id: `chart-${Date.now()}`,
      name: defaultData.title,
    });

    canvas.add(img);
    canvas.setActiveObject(img);
    canvas.renderAll();
    syncLayers();
    pushHistory();
  };

  return (
    <div style={panelStyle}>
      <h3 style={titleStyle}>차트</h3>
      <div style={sectionStyle}>
        <label style={labelStyle}>차트 추가</label>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
          {CHART_TYPES.map(t => (
            <button
              key={t.id}
              onClick={() => handleAddChart(t.id)}
              style={{
                padding: '14px 8px',
                fontSize: '13px',
                fontWeight: '500',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                backgroundColor: '#fff',
                color: '#374151',
                cursor: 'pointer',
                transition: 'all 0.15s',
              }}
              onMouseEnter={(e) => { e.target.style.backgroundColor = '#eff6ff'; e.target.style.borderColor = '#3b82f6'; }}
              onMouseLeave={(e) => { e.target.style.backgroundColor = '#fff'; e.target.style.borderColor = '#e5e7eb'; }}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>
      <div style={{ ...sectionStyle, textAlign: 'center', color: '#9ca3af', padding: '20px 0' }}>
        <p style={{ fontSize: '12px' }}>
          차트를 선택하면 편집할 수 있습니다.
        </p>
      </div>
    </div>
  );
};

// --- 차트 편집 UI (차트 선택 시) ---
const ChartSettingsPanel = () => {
  const { canvas, selectedObjects, pushHistory } = useEditorStore();
  const selectedChart = selectedObjects.find(obj => obj._isChart);

  // 차트 데이터 상태
  const [chartType, setChartType] = useState('bar');
  const [chartSubType, setChartSubType] = useState('basic');
  const [chartTitle, setChartTitle] = useState('');
  const [titleFontSize, setTitleFontSize] = useState(20);
  const [titleColor, setTitleColor] = useState('#333333');
  const [series, setSeries] = useState([]);
  const [legendPos, setLegendPos] = useState('b');
  const [dataPointColors, setDataPointColors] = useState(null);

  // 선택된 차트에서 상태 로드
  useEffect(() => {
    if (!selectedChart) return;
    setChartType(selectedChart._chartType || 'bar');
    setChartSubType(selectedChart._chartSubType || 'basic');
    setChartTitle(selectedChart._chartTitle || '');
    setTitleFontSize(selectedChart._chartTitleFontSize || 20);
    setTitleColor(selectedChart._chartTitleColor || '#333333');
    setLegendPos(parseJSON(selectedChart._chartLegend)?.position || 'none');
    setSeries(parseJSON(selectedChart._chartSeries, []));
    setDataPointColors(parseJSON(selectedChart._chartDataPointColors));
  }, [selectedChart]);

  // 차트 다시 렌더링
  const reRenderChart = useCallback(async (newData) => {
    if (!selectedChart || !canvas) return;

    const currentSeries = newData.series ?? series;
    const currentDpc = newData.dataPointColors !== undefined ? newData.dataPointColors : dataPointColors;

    const chartData = {
      chartType: newData.chartType ?? chartType,
      chartSubType: newData.chartSubType ?? chartSubType,
      title: newData.title !== undefined ? newData.title : chartTitle,
      titleFontSize: newData.titleFontSize ?? titleFontSize,
      titleColor: newData.titleColor ?? titleColor,
      series: currentSeries,
      axes: parseJSON(selectedChart._chartAxes, []),
      legend: (newData.legendPos ?? legendPos) === 'none'
        ? null
        : { position: newData.legendPos ?? legendPos },
      dataPointColors: currentDpc,
      gapWidth: selectedChart._chartGapWidth,
      overlap: selectedChart._chartOverlap,
    };

    const w = (selectedChart.width || 400) * (selectedChart.scaleX || 1);
    const h = (selectedChart.height || 300) * (selectedChart.scaleY || 1);

    const img = await createChartFabricImage(chartData, w, h);

    // 기존 위치/크기 보존하면서 이미지 교체
    selectedChart.setElement(img.getElement());
    selectedChart._originalElement = img.getElement();
    selectedChart.set({
      scaleX: w / img.width,
      scaleY: h / img.height,
    });

    // 메타데이터 업데이트
    selectedChart._chartType = chartData.chartType;
    selectedChart._chartSubType = chartData.chartSubType;
    selectedChart._chartTitle = chartData.title;
    selectedChart._chartTitleFontSize = chartData.titleFontSize;
    selectedChart._chartTitleColor = chartData.titleColor;
    selectedChart._chartSeries = JSON.stringify(chartData.series);
    selectedChart._chartLegend = chartData.legend ? JSON.stringify(chartData.legend) : null;
    selectedChart._chartDataPointColors = currentDpc ? JSON.stringify(currentDpc) : null;
    selectedChart.name = chartData.title || selectedChart.name;

    canvas.renderAll();
    pushHistory();
  }, [selectedChart, canvas, chartType, chartSubType, chartTitle, titleFontSize, titleColor, series, legendPos, dataPointColors, pushHistory]);

  // --- 핸들러 ---
  const handleChartTypeChange = (newType) => {
    setChartType(newType);
    const validSubs = CHART_SUB_TYPES[newType] || [];
    const newSub = validSubs.find(s => s.id === chartSubType) ? chartSubType : 'basic';
    setChartSubType(newSub);

    // 파이로 전환 시: dataPointColors 자동 생성
    let newDpc = dataPointColors;
    if (newType === 'pie' && !dataPointColors && series.length > 0) {
      const catCount = series[0].categories.length;
      newDpc = Array.from({ length: catCount }, (_, i) => DEFAULT_CHART_COLORS[i % DEFAULT_CHART_COLORS.length]);
      setDataPointColors(newDpc);
    }

    reRenderChart({ chartType: newType, chartSubType: newSub, dataPointColors: newDpc });
  };

  const handleSubTypeChange = (newSub) => {
    setChartSubType(newSub);
    reRenderChart({ chartSubType: newSub });
  };

  const handleTitleChange = (val) => setChartTitle(val);
  const handleTitleBlur = () => reRenderChart({ title: chartTitle });

  const handleTitleFontSizeChange = (size) => {
    const s = parseInt(size) || 20;
    setTitleFontSize(s);
    reRenderChart({ titleFontSize: s });
  };

  const handleTitleColorChange = (color) => {
    setTitleColor(color);
    reRenderChart({ titleColor: color });
  };

  const handleLegendChange = (pos) => {
    setLegendPos(pos);
    reRenderChart({ legendPos: pos });
  };

  // === 시리즈 핸들러 (비파이) ===
  const handleSeriesNameChange = (idx, name) => {
    const newSeries = [...series];
    newSeries[idx] = { ...newSeries[idx], name };
    setSeries(newSeries);
  };

  const handleSeriesColorChange = (idx, color) => {
    const newSeries = [...series];
    newSeries[idx] = { ...newSeries[idx], color };
    setSeries(newSeries);
    reRenderChart({ series: newSeries });
  };

  const handleValueChange = (serIdx, valIdx, value) => {
    const newSeries = [...series];
    const newValues = [...newSeries[serIdx].values];
    newValues[valIdx] = parseFloat(value) || 0;
    newSeries[serIdx] = { ...newSeries[serIdx], values: newValues };
    setSeries(newSeries);
  };

  const handleCategoryChange = (catIdx, name) => {
    const newSeries = series.map(s => {
      const newCats = [...s.categories];
      newCats[catIdx] = name;
      return { ...s, categories: newCats };
    });
    setSeries(newSeries);
  };

  const handleAddSeries = () => {
    const catCount = series.length > 0 ? series[0].categories.length : 4;
    const newIdx = series.length;
    const newSer = {
      index: newIdx, order: newIdx,
      name: `계열 ${newIdx + 1}`,
      color: DEFAULT_CHART_COLORS[newIdx % DEFAULT_CHART_COLORS.length],
      categories: series.length > 0 ? [...series[0].categories] : Array(catCount).fill('').map((_, i) => `항목 ${i + 1}`),
      values: Array(catCount).fill(0),
    };
    const newSeries = [...series, newSer];
    setSeries(newSeries);
    reRenderChart({ series: newSeries });
  };

  const handleDeleteSeries = (idx) => {
    if (series.length <= 1) return;
    const newSeries = series.filter((_, i) => i !== idx).map((s, i) => ({ ...s, index: i, order: i }));
    setSeries(newSeries);
    reRenderChart({ series: newSeries });
  };

  const handleAddCategory = () => {
    const newSeries = series.map(s => ({
      ...s,
      categories: [...s.categories, `항목 ${s.categories.length + 1}`],
      values: [...s.values, 0],
    }));
    setSeries(newSeries);
    if (chartType === 'pie' && dataPointColors) {
      const newDpc = [...dataPointColors, DEFAULT_CHART_COLORS[dataPointColors.length % DEFAULT_CHART_COLORS.length]];
      setDataPointColors(newDpc);
      reRenderChart({ series: newSeries, dataPointColors: newDpc });
    } else {
      reRenderChart({ series: newSeries });
    }
  };

  const handleDeleteCategory = (catIdx) => {
    if (series.length > 0 && series[0].categories.length <= 1) return;
    const newSeries = series.map(s => ({
      ...s,
      categories: s.categories.filter((_, i) => i !== catIdx),
      values: s.values.filter((_, i) => i !== catIdx),
    }));
    setSeries(newSeries);
    if (chartType === 'pie' && dataPointColors) {
      const newDpc = dataPointColors.filter((_, i) => i !== catIdx);
      setDataPointColors(newDpc);
      reRenderChart({ series: newSeries, dataPointColors: newDpc });
    } else {
      reRenderChart({ series: newSeries });
    }
  };

  // === 파이 차트 전용 ===
  const handlePieSliceColorChange = (catIdx, color) => {
    const newDpc = [...(dataPointColors || [])];
    newDpc[catIdx] = color;
    setDataPointColors(newDpc);
    reRenderChart({ dataPointColors: newDpc });
  };

  const handlePieSliceValueChange = (catIdx, value) => {
    if (series.length === 0) return;
    const newSeries = [...series];
    const newValues = [...newSeries[0].values];
    newValues[catIdx] = parseFloat(value) || 0;
    newSeries[0] = { ...newSeries[0], values: newValues };
    setSeries(newSeries);
  };

  // blur 공용 핸들러
  const handleBlur = () => reRenderChart({ series });

  // --- 차트 미선택 → 추가 UI ---
  if (!selectedChart) return <ChartAddSection />;

  const categories = series.length > 0 ? series[0].categories : [];
  const isPie = chartType === 'pie';

  return (
    <div style={panelStyle}>
      <h3 style={titleStyle}>차트 설정</h3>

      {/* 차트 타입 */}
      <div style={sectionStyle}>
        <label style={labelStyle}>차트 타입</label>
        <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
          {CHART_TYPES.map(t => (
            <button
              key={t.id}
              onClick={() => handleChartTypeChange(t.id)}
              style={chipStyle(chartType === t.id)}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* 서브타입 */}
      {CHART_SUB_TYPES[chartType]?.length > 1 && (
        <div style={sectionStyle}>
          <label style={labelStyle}>서브타입</label>
          <div style={{ display: 'flex', gap: '4px' }}>
            {CHART_SUB_TYPES[chartType].map(s => (
              <button
                key={s.id}
                onClick={() => handleSubTypeChange(s.id)}
                style={{ ...chipStyle(chartSubType === s.id), flex: 1 }}
              >
                {s.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* 제목 */}
      <div style={sectionStyle}>
        <label style={labelStyle}>제목</label>
        <input
          type="text"
          value={chartTitle || ''}
          onChange={(e) => handleTitleChange(e.target.value)}
          onBlur={handleTitleBlur}
          onKeyDown={(e) => e.key === 'Enter' && handleTitleBlur()}
          placeholder="차트 제목"
          style={{ ...inputStyle, width: '100%' }}
        />
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginTop: '6px' }}>
          <ColorPicker value={titleColor} onChange={handleTitleColorChange} showGradient={false} size="default" />
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <label style={{ fontSize: '11px', color: '#6b7280' }}>크기</label>
            <input
              type="number"
              value={titleFontSize}
              onChange={(e) => handleTitleFontSizeChange(e.target.value)}
              style={smallInputStyle}
              min="8" max="72"
            />
            <span style={{ fontSize: '11px', color: '#9ca3af' }}>pt</span>
          </div>
        </div>
      </div>

      {/* 범례 */}
      <div style={sectionStyle}>
        <label style={labelStyle}>범례</label>
        <select value={legendPos} onChange={(e) => handleLegendChange(e.target.value)} style={selectStyle}>
          {LEGEND_POSITIONS.map(p => <option key={p.id} value={p.id}>{p.label}</option>)}
        </select>
      </div>

      {/* 데이터 편집: 파이 vs 비파이 */}
      {isPie ? (
        <PieDataSection
          categories={categories}
          series={series}
          dataPointColors={dataPointColors}
          onSliceColorChange={handlePieSliceColorChange}
          onSliceValueChange={handlePieSliceValueChange}
          onCategoryChange={handleCategoryChange}
          onAddCategory={handleAddCategory}
          onDeleteCategory={handleDeleteCategory}
          onBlur={handleBlur}
        />
      ) : (
        <SeriesDataSection
          series={series}
          categories={categories}
          onSeriesNameChange={handleSeriesNameChange}
          onSeriesColorChange={handleSeriesColorChange}
          onValueChange={handleValueChange}
          onCategoryChange={handleCategoryChange}
          onAddSeries={handleAddSeries}
          onDeleteSeries={handleDeleteSeries}
          onAddCategory={handleAddCategory}
          onDeleteCategory={handleDeleteCategory}
          onBlur={handleBlur}
        />
      )}

      {/* 새로고침 버튼 */}
      <div style={sectionStyle}>
        <button onClick={() => reRenderChart({})} style={refreshBtnStyle}>
          <FiRefreshCw size={14} />
          차트 새로고침
        </button>
      </div>
    </div>
  );
};

// --- 파이 차트 슬라이스 데이터 UI ---
const PieDataSection = ({ categories, series, dataPointColors, onSliceColorChange, onSliceValueChange, onCategoryChange, onAddCategory, onDeleteCategory, onBlur }) => (
  <div style={sectionStyle}>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
      <label style={{ ...labelStyle, marginBottom: 0 }}>슬라이스</label>
      <button onClick={onAddCategory} style={iconBtnStyle} title="슬라이스 추가"><FiPlus size={14} /></button>
    </div>
    {categories.map((cat, cIdx) => {
      const sliceColor = (dataPointColors && dataPointColors[cIdx]) || DEFAULT_CHART_COLORS[cIdx % DEFAULT_CHART_COLORS.length];
      const sliceValue = series.length > 0 ? (series[0].values[cIdx] ?? 0) : 0;
      return (
        <div key={cIdx} style={cardStyle}>
          <div style={{ display: 'flex', gap: '6px', alignItems: 'center', marginBottom: '6px' }}>
            <ColorPicker value={sliceColor} onChange={(c) => onSliceColorChange(cIdx, c)} showGradient={false} size="default" />
            <input
              type="text" value={cat}
              onChange={(e) => onCategoryChange(cIdx, e.target.value)}
              onBlur={onBlur}
              style={{ ...inputStyle, fontSize: '12px', padding: '4px 8px', flex: 1 }}
              placeholder={`항목 ${cIdx + 1}`}
            />
            {categories.length > 1 && (
              <button onClick={() => onDeleteCategory(cIdx)} style={{ ...iconBtnStyle, color: '#ef4444' }} title="삭제"><FiMinus size={12} /></button>
            )}
          </div>
          <input
            type="number" value={sliceValue}
            onChange={(e) => onSliceValueChange(cIdx, e.target.value)}
            onBlur={onBlur}
            style={{ ...smallInputStyle, width: '80px' }}
            step="0.1"
          />
        </div>
      );
    })}
  </div>
);

// --- 비파이 차트 시리즈+카테고리 데이터 UI ---
const SeriesDataSection = ({ series, categories, onSeriesNameChange, onSeriesColorChange, onValueChange, onCategoryChange, onAddSeries, onDeleteSeries, onAddCategory, onDeleteCategory, onBlur }) => (
  <>
    {/* 시리즈 */}
    <div style={sectionStyle}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
        <label style={{ ...labelStyle, marginBottom: 0 }}>시리즈</label>
        <button onClick={onAddSeries} style={iconBtnStyle} title="시리즈 추가"><FiPlus size={14} /></button>
      </div>
      {series.map((s, sIdx) => (
        <div key={sIdx} style={{ ...cardStyle, marginBottom: '12px' }}>
          <div style={{ display: 'flex', gap: '6px', alignItems: 'center', marginBottom: '6px' }}>
            <ColorPicker value={s.color} onChange={(c) => onSeriesColorChange(sIdx, c)} showGradient={false} size="default" />
            <input
              type="text" value={s.name || ''}
              onChange={(e) => onSeriesNameChange(sIdx, e.target.value)}
              onBlur={onBlur}
              style={{ ...inputStyle, fontSize: '12px', padding: '4px 8px' }}
              placeholder={`계열 ${sIdx + 1}`}
            />
            {series.length > 1 && (
              <button onClick={() => onDeleteSeries(sIdx)} style={{ ...iconBtnStyle, color: '#ef4444' }} title="삭제"><FiMinus size={12} /></button>
            )}
          </div>
          <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
            {s.values.map((v, vIdx) => (
              <input
                key={vIdx} type="number" value={v}
                onChange={(e) => onValueChange(sIdx, vIdx, e.target.value)}
                onBlur={onBlur}
                style={{ ...smallInputStyle, width: '52px' }}
                step="0.1"
              />
            ))}
          </div>
        </div>
      ))}
    </div>

    {/* 카테고리 */}
    <div style={sectionStyle}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
        <label style={{ ...labelStyle, marginBottom: 0 }}>카테고리</label>
        <button onClick={onAddCategory} style={iconBtnStyle} title="카테고리 추가"><FiPlus size={14} /></button>
      </div>
      {categories.map((cat, cIdx) => (
        <div key={cIdx} style={{ display: 'flex', gap: '4px', marginBottom: '4px', alignItems: 'center' }}>
          <input
            type="text" value={cat}
            onChange={(e) => onCategoryChange(cIdx, e.target.value)}
            onBlur={onBlur}
            style={{ ...inputStyle, fontSize: '12px', padding: '4px 8px' }}
            placeholder={`항목 ${cIdx + 1}`}
          />
          {categories.length > 1 && (
            <button onClick={() => onDeleteCategory(cIdx)} style={{ ...iconBtnStyle, color: '#ef4444' }} title="삭제"><FiMinus size={12} /></button>
          )}
        </div>
      ))}
    </div>
  </>
);

// --- 공용 스타일 ---
const chipStyle = (active) => ({
  padding: '6px 10px',
  fontSize: '12px',
  fontWeight: active ? '600' : '400',
  border: active ? '2px solid #3b82f6' : '1px solid #d1d5db',
  borderRadius: '6px',
  backgroundColor: active ? '#eff6ff' : '#fff',
  color: active ? '#3b82f6' : '#374151',
  cursor: 'pointer',
});

const iconBtnStyle = {
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  width: '24px', height: '24px',
  border: '1px solid #d1d5db', borderRadius: '4px',
  backgroundColor: '#fff', cursor: 'pointer', color: '#374151', flexShrink: 0,
};

const cardStyle = {
  marginBottom: '8px', padding: '8px',
  backgroundColor: '#f9fafb', borderRadius: '8px', border: '1px solid #e5e7eb',
};

const smallInputStyle = {
  padding: '3px 4px', border: '1px solid #e5e7eb', borderRadius: '4px',
  fontSize: '11px', textAlign: 'center',
};

const refreshBtnStyle = {
  width: '100%', padding: '10px',
  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
  backgroundColor: '#3b82f6', color: '#fff',
  border: 'none', borderRadius: '8px', cursor: 'pointer',
  fontSize: '13px', fontWeight: '600',
};

export default ChartSettingsPanel;
